import { Outlet, createRootRoute, createRoute, createRouter, lazyRouteComponent } from "@tanstack/react-router";
import { RootPage } from "./pages/RootPage";
import { TrimPage } from "./pages/TrimPage";
import { BoardPage } from "./pages/BoardPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ConsolePage } from "./pages/ConsolePage";
import { MarketingPage, NotFoundPage, PricingPage, TourPage } from "./pages/site/MarketingPages";
import { ContactPage } from "./pages/site/ContactPage";
import { AccountPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/site/AccountPages";
import { LegalPage } from "./pages/site/LegalPages";
const TrimRoomPage = lazyRouteComponent(() => import("./pages/TrimRoomPage"), "TrimRoomPage");
import { BillingPage } from "./pages/BillingPage";
import { TrimPayCalculatorPage } from "./pages/site/TrimPayCalculator";
import { GoPage } from "./pages/site/GoPage";
import { InvitePage } from "./pages/InvitePage";
const SeasonCardPage = lazyRouteComponent(() => import("./pages/SeasonCardPage"), "SeasonCardPage");

// Code-based route tree (no file-based codegen plugin) — hand-wiring
// createRoute() calls is simpler than pulling in @tanstack/router-plugin's
// build-time generation for an app this size. M5b adds the three
// manager-facing screens (board/dashboard/console); each page does its own
// auth/role gating inline (see useAuth's role/isOwner) rather than a
// router-level beforeLoad guard, matching HomePage's existing
// loading/player-gated render pattern.
const rootRoute = createRootRoute({ component: () => <Outlet />, notFoundComponent: NotFoundPage });

const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: RootPage });
const trimRoute = createRoute({ getParentRoute: () => rootRoute, path: "/trim", component: TrimPage });
const boardRoute = createRoute({ getParentRoute: () => rootRoute, path: "/board", component: BoardPage });
const dashboardRoute = createRoute({ getParentRoute: () => rootRoute, path: "/dashboard", component: DashboardPage });
const consoleRoute = createRoute({ getParentRoute: () => rootRoute, path: "/console", component: ConsolePage });

const siteRoute = createRoute({ getParentRoute: () => rootRoute, path: "/site", component: MarketingPage });
const pricingRoute = createRoute({ getParentRoute: () => rootRoute, path: "/pricing", component: PricingPage });
const tourRoute = createRoute({ getParentRoute: () => rootRoute, path: "/tour", component: TourPage });
const contactRoute = createRoute({ getParentRoute: () => rootRoute, path: "/contact", component: ContactPage });
const signupRoute = createRoute({ getParentRoute: () => rootRoute, path: "/signup", component: () => <AccountPage mode="signup" /> });
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: () => <AccountPage mode="login" /> });
const forgotRoute = createRoute({ getParentRoute: () => rootRoute, path: "/forgot", component: ForgotPasswordPage });
const resetRoute = createRoute({ getParentRoute: () => rootRoute, path: "/reset", component: ResetPasswordPage });
const privacyRoute = createRoute({ getParentRoute: () => rootRoute, path: "/privacy", component: () => <LegalPage kind="privacy" /> });
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/terms", component: () => <LegalPage kind="terms" /> });
const billingRoute = createRoute({ getParentRoute: () => rootRoute, path: "/billing", component: BillingPage });
const trimRoomRoute = createRoute({ getParentRoute: () => rootRoute, path: "/trim-room", component: TrimRoomPage });
const payCalculatorRoute = createRoute({ getParentRoute: () => rootRoute, path: "/trim-pay-calculator", component: TrimPayCalculatorPage });
const goRoute = createRoute({ getParentRoute: () => rootRoute, path: "/go/$code", component: () => <GoPage /> });
const igRoute = createRoute({ getParentRoute: () => rootRoute, path: "/ig", component: () => <GoPage code="ig" /> });
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: "/i/$token", component: InvitePage });
const seasonRoute = createRoute({ getParentRoute: () => rootRoute, path: "/season", component: SeasonCardPage });
const routeTree = rootRoute.addChildren([homeRoute, trimRoute, boardRoute, dashboardRoute, consoleRoute, siteRoute, pricingRoute, tourRoute, contactRoute, signupRoute, loginRoute, forgotRoute, resetRoute, privacyRoute, termsRoute, billingRoute, trimRoomRoute, payCalculatorRoute, goRoute, igRoute, seasonRoute, inviteRoute]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
